"use client";

import Link from "next/link";
import { useRef } from "react";
import { Tag } from "primereact/tag";

import { Column } from "primereact/column";

import orderApi from "@/api/order";
import { startDate, endDate } from "@/lib/dayjs";
import EnhancedTable from "@/hocs/EnhancedTable";
import customizationApi from "@/api/customization";
import { Order } from "@/interfaces/order.interface";
import { useToastStore } from "@/stores/toast.store";
import { NO_IMAGE_SRC_PATH } from "@/constants/image";
import { filterOrderOptions } from "@/constants/filter";
import useTimezoneStore from "@/stores/timezone.store";
import CustomButton from "@/components/common/CustomButton";
import OrderPropertiesModal from "@/components/OrderPropertiesModal";
import { copyToClipboard, formatDateWithTimezone } from "@/utils/func";
import { orderStatusConfig, printStatusConfig } from "@/constants/status";
import { MoveUpRight } from "lucide-react";
import Image from "next/image";

const ROWS_PER_PAGE = 10;

const OrdersPage = () => {
  const refetchRef = useRef<(() => void) | null>(null);

  const displayTimeZone = useTimezoneStore((s) => s.timezone);

  const handlePrintImage = async (customizationId: string) => {
    try {
      await customizationApi.printManually(customizationId);
      useToastStore
        .getState()
        .showToast(
          "info",
          "Print job queued",
          "Your print request has been added to the processing queue",
        );
      refetchRef.current?.();
    } catch (error) {
      console.log(error);
      useToastStore
        .getState()
        .showToast(
          "error",
          "Failed to queue print job",
          "Please try again later",
        );
    }
  };

  return (
    <div className="flex h-full w-full flex-col rounded-lg bg-white p-6 shadow-lg">
      <EnhancedTable
        size="small"
        cachedKey="Orders"
        fetchData={orderApi.getMany}
        itemsPerPage={ROWS_PER_PAGE}
        searchPlaceholder="Search orders by id, customization id, order id"
        title="Orders"
        scrollable
        filterOptions={filterOrderOptions}
        dateConfig={{
          fields: ["startDate", "endDate"],
          defaultValues: [startDate.toDate(), endDate.toDate()],
        }}
        refetchRef={refetchRef}
      >
        <Column
          header="Preview"
          field="customizationInfo.previewImageUrl"
          bodyClassName="text-center"
          body={(rowData: Order) => (
            <div className="group relative flex h-16 w-16 cursor-pointer rounded border border-gray-300">
              <Image
                crossOrigin="anonymous"
                width={200}
                height={200}
                src={
                  rowData?.customization?.previewImageUrl || NO_IMAGE_SRC_PATH
                }
                alt="Preview Image"
                className="transition-opacity hover:opacity-80"
                loading="lazy"
              />

              <div
                className="absolute hidden h-full w-full items-center justify-center gap-2 bg-[#696b7073] group-hover:flex"
                onMouseUp={(e) => {
                  if (e.button === 1) {
                    if (rowData?.customization?.previewImageUrl) {
                      e.preventDefault();
                      window.open(
                        rowData.customization?.previewImageUrl,
                        "_blank",
                      );
                    }
                  }
                }}
                onClick={() => {
                  if (rowData?.customization?.previewImageUrl) {
                    window.open(
                      rowData.customization?.previewImageUrl,
                      "_blank",
                    );
                  }
                }}
              >
                <MoveUpRight size={20} />
              </div>
            </div>
          )}
        />

        <Column
          header="Print Image"
          field="customizationInfo.printImageUrl"
          body={(rowData: Order) => (
            <div className="group relative flex h-16 w-16 cursor-pointer rounded border border-gray-300">
              <Image
                crossOrigin="anonymous"
                width={200}
                height={200}
                src={rowData?.customization?.printImageUrl || NO_IMAGE_SRC_PATH}
                alt="Print Image"
                className="transition-opacity hover:opacity-80"
                loading="lazy"
              />

              <div
                className="absolute hidden h-full w-full items-center justify-center gap-2 bg-[#696b7073] group-hover:flex"
                onMouseUp={(e) => {
                  if (e.button === 1) {
                    if (rowData?.customization?.printImageUrl) {
                      e.preventDefault();
                      window.open(
                        rowData.customization?.printImageUrl,
                        "_blank",
                      );
                    }
                  }
                }}
                onClick={() => {
                  if (rowData?.customization?.printImageUrl) {
                    window.open(rowData.customization?.printImageUrl, "_blank");
                  }
                }}
              >
                <MoveUpRight size={20} />
              </div>
            </div>
          )}
        />

        <Column
          field="name"
          header="Order Name"
          body={(rowData: Order) => (
            <div>
              <p className="text-gray-800">{rowData.orderName}</p>
              <span
                className="cursor-pointer text-sm text-gray-500 hover:text-zinc-700"
                title="Click to copy ID"
                onClick={(e) => {
                  e.stopPropagation();
                  copyToClipboard(rowData.orderId);
                }}
              >
                ID: {rowData.orderId}
              </span>
            </div>
          )}
        />
        <Column
          field="productTitle"
          header="Campaign"
          style={{
            minWidth: "250px",
            maxWidth: "250px",
            textAlign: "start",
          }}
          body={(rowData: Order) => (
            <div>
              <Link
                href={`/campaigns/${rowData.customization?.campaignId}`}
                target="_blank"
                className="line-clamp-2 w-full transition-all hover:underline"
              >
                <p className="text-gray-800">{rowData.productTitle || "N/A"}</p>
              </Link>
              {rowData.productSKU && (
                <span
                  className="cursor-pointer text-sm text-gray-500 hover:text-zinc-700"
                  title="Click to copy Slug"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (rowData.productSKU) {
                      copyToClipboard(rowData.productSKU);
                    }
                  }}
                >
                  SKU: {rowData.productSKU}
                </span>
              )}
            </div>
          )}
        />

        <Column
          header="Properties"
          field="orderId"
          bodyClassName="text-center"
          body={(rowData: Order) => {
            return <OrderPropertiesModal orderId={rowData._id!} />;
          }}
          style={{
            width: "150px",
            whiteSpace: "nowrap",
            textAlign: "center",
          }}
        />
        <Column
          header="Status"
          field="status"
          style={{ width: "120px", whiteSpace: "nowrap", textAlign: "start" }}
          body={(rowData: Order) => {
            const statusKey = rowData.status || "default";
            const config =
              orderStatusConfig[statusKey] || orderStatusConfig.default;
            return (
              <Tag
                value={config.label}
                severity={config.severity}
                icon={config.icon}
                style={{ backgroundColor: config.color, color: "white" }}
              />
            );
          }}
        />
        <Column
          header="Print Status"
          field="customizationInfo.printStatus"
          style={{ width: "140px", whiteSpace: "nowrap", textAlign: "start" }}
          body={(rowData: Order) => {
            const statusKey = rowData.customization?.printStatus || "default";
            const config =
              printStatusConfig[statusKey] || printStatusConfig.default;
            return (
              <Tag
                value={config.label}
                severity={config.severity}
                icon={config.icon}
                style={{ backgroundColor: config.color, color: "white" }}
              />
            );
          }}
        />
        <Column
          header="Order Date"
          field="orderCreatedAt"
          style={{ width: "150px", whiteSpace: "nowrap", textAlign: "start" }}
          body={(rowData: Order) => (
            <span className="text-sm text-gray-500">
              {formatDateWithTimezone(rowData.orderCreatedAt, displayTimeZone)}
            </span>
          )}
        />
        <Column
          header="Actions"
          frozen
          alignFrozen="right"
          style={{ width: "80px" }}
          bodyClassName="text-center"
          body={(order: Order) => (
            <div className="flex items-center justify-center gap-2">
              <CustomButton
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full hover:bg-gray-200"
                onClick={() => {
                  if (order.customizationId) {
                    handlePrintImage(order.customizationId);
                  }
                }}
                tooltip="Print image"
                tooltipOptions={{ position: "top" }}
              >
                <i className="pi pi-print text-lg text-blue-600" />
              </CustomButton>
            </div>
          )}
        />
      </EnhancedTable>
    </div>
  );
};

export default OrdersPage;
